/* eslint-disable max-len */
import { Component, Host, h, Prop, Element, Watch } from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

/**
 * MapMarkers are used to mark a specific location on a map. They can be used in combination with the Map component.
 */

@Component({
  tag: 'ui-map-marker',
  styleUrl: 'ui-map-marker.scss',
  shadow: true,
})
export class UiMapMarker {
  @Element() el: HTMLElement;

  /**
   * Represents the icon's variant, relates to the color of the icon itself. The default is *colored*.
   */
  @Prop({ reflect: true }) variant: 'main' | 'place' | 'cluster' = 'main';

  /**
   * If the marker has to be colored from another component, this property will be used to set the color.
   * Currently this only changes something for the *place* variant.
   */
  @Prop({ reflect: true }) state: 'default' | 'hover' | 'active' = 'default';

  /**
   * If the marker has to be animated, this property will be used to set the animation.
   */
  @Prop({ reflect: true }) animate = false;

  animatableElement: HTMLElement;

  componentDidLoad() {
    this.animateHandler();
  }

  @Watch('animate')
  animateHandler() {
    if (this.animate) {
      const animation = this.animatableElement.animate(
        [
          { transform: 'translateY(0)' },
          { transform: 'translateY(-9px)' },
          { transform: 'translateY(-12px)' },
          { transform: 'translateY(-4px)' },
          { transform: 'translateY(0)' },
          { transform: 'translateY(-4px)' },
          { transform: 'translateY(-6px)' },
          { transform: 'translateY(-2px)' },
          { transform: 'translateY(0)' },
        ],
        {
          duration: 600,
          iterations: 1,
        }
      );

      // remove the animate prop after the animation
      animation.finished.then(() => {
        this.animate = false;
      });
    }
  }

  svg = {
    cluster: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
    <circle cx="25" cy="25" opacity="1" r="20" />
    <circle cx="25" cy="25" opacity=".3" r="25" />
  </svg>`,
    main: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 75">
    <path fill-rule="evenodd" d="M51 49A29 29 0 0 0 30 0 29 29 0 0 0 9 49l21 22 21-22ZM40 28a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z" clip-rule="evenodd"/>
  </svg>`,
    place: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 75">
    <path fill-rule="evenodd" d="M51 49A29 29 0 0 0 30 0 29 29 0 0 0 9 49l21 22 21-22Z" clip-rule="evenodd"/>
  </svg>`,
  };

  render() {
    return (
      <Host>
        <div
          ref={(el) => {
            this.animatableElement = el;
          }}
        >
          <div class="marker" innerHTML={this.svg[this.variant]}></div>
          <div class="content">
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}

versionComponent(UiMapMarker);
